import { Contexto } from '../componentes/Contexto';
import { Filtros } from '../componentes/Filtros';
import { Icono } from '../componentes/Icono';
import { Segmentos } from '../componentes/Segmentos';
import { TarjetaCifra } from '../componentes/TarjetaCifra';
import { cifraDinero, dinero } from '../logica/dinero';
import { useCabecera } from '../marco/cabecera';
import {
    SUBTITULOS,
    ayudaDe,
    claveMes,
    comisionesDelMes,
    estadoComision,
    lineaComision,
    lineaLiquidacion,
    liquidacionDelMes,
    liquidacionEnCurso,
    mesCorto,
    mesDe,
    mesDePeriodo,
    mesSiguiente,
    nombreMes,
    textoMes,
    variacion,
    type Mes,
    type VistaComisiones,
} from './logica';
import { BarrasMes, Fila } from './piezas';
import { usePorClave, type DatosPortal } from './carga';

const mayuscula = (t: string): string => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * Comisiones (con su apartado Liquidaciones). El mes elegido (RV Contexto, en la banda; `?mes=AAAA-MM`) manda en toda la
 * pantalla y las comisiones se piden ya filtradas por ese mes (`period`, G1098) y, desde «Ver sus comisiones» de
 * Negocios, por su negocio (`?negocio=`, filtro `business_id`, G1104). Cada cifra es de la API: «Cobrado a tus negocios»
 * («Cobrado a <negocio>» con uno elegido), «Tu comisión» y su variación salen de `getPartnerSummary` con el `period` del mes
 * elegido y, con un negocio, su `business_id`: las tarjetas son las de ese negocio (G1094, G1105, G1177; brutas, G1116);
 * si el resumen no llega, «Tu comisión» es el total de su liquidación. Cada barra es el total de la liquidación de su mes y
 * cada línea la comisión de un negocio; con un negocio elegido no hay barras (G1199).
 */
export function Comisiones({ d, vista }: { d: DatosPortal; vista: VistaComisiones }) {
    const liquidaciones = d.liquidaciones.datos?.data ?? [];
    const hoy = mesDe(new Date().toISOString());
    // Meses del selector: el de hoy y los de cada liquidación, del más reciente al más antiguo.
    const meses = new Map<string, Mes>([[claveMes(hoy), hoy]]);
    for (const l of liquidaciones) {
        const m = mesDePeriodo(l.period);
        meses.set(claveMes(m), m);
    }
    const opciones = [...meses.entries()].sort(([a], [b]) => b.localeCompare(a)).map(([valor, m]) => ({ valor, texto: textoMes(m) }));
    const pedido = d.busqueda.get('mes');
    const mes = (pedido !== null ? meses.get(pedido) : undefined) ?? hoy;
    const negocio = d.busqueda.get('negocio');
    // Cambiar de mes conserva el negocio elegido.
    const elegirMes = (valor: string) => {
        d.navegar(`${d.ruta}?seccion=comisiones&mes=${encodeURIComponent(valor)}${negocio === null ? '' : `&negocio=${encodeURIComponent(negocio)}`}`);
    };
    const quitarNegocio = () => {
        d.navegar(`${d.ruta}?seccion=comisiones&mes=${encodeURIComponent(claveMes(mes))}`);
    };
    const enLiquidaciones = vista === 'liquidaciones';

    useCabecera({
        ayuda: ayudaDe(enLiquidaciones ? 'liquidaciones' : 'comisiones', d.pct),
        acciones: [],
        subtitulo: enLiquidaciones ? SUBTITULOS.liquidaciones : SUBTITULOS.comisiones.replace('{pct}', d.pct),
        contexto: enLiquidaciones || d.movil ? [] : [{ clave: 'mes', etiqueta: 'Mes', valor: claveMes(mes), opciones, icono: 'calendar', onChange: elegirMes }],
    });

    return (
        <div className="rv-pt-seccion">
            <Segmentos
                etiqueta="Comisiones"
                movil={d.movil}
                items={[
                    { etiqueta: 'Comisiones', icono: 'percent' },
                    { etiqueta: 'Liquidaciones', icono: 'wallet' },
                ]}
                valor={enLiquidaciones ? 1 : 0}
                onCambio={(i) => {
                    d.ir(i === 1 ? 'liquidaciones' : 'comisiones');
                }}
            />
            {enLiquidaciones ? (
                <Liquidaciones d={d} />
            ) : (
                <>
                    {d.movil && <Contexto etiqueta="Mes" valor={claveMes(mes)} opciones={opciones} icono="calendar" movil onChange={elegirMes} />}
                    <DelMes d={d} mes={mes} hoy={hoy} negocio={negocio} elegirMes={elegirMes} quitarNegocio={quitarNegocio} />
                </>
            )}
        </div>
    );
}

function DelMes({ d, mes, hoy, negocio, elegirMes, quitarNegocio }: { d: DatosPortal; mes: Mes; hoy: Mes; negocio: string | null; elegirMes: (valor: string) => void; quitarNegocio: () => void }) {
    const liquidaciones = d.liquidaciones.datos?.data ?? [];
    const liquidacion = liquidacionDelMes(liquidaciones, mes);
    const sig = nombreMes(mesSiguiente(mes));
    const esHoy = claveMes(mes) === claveMes(hoy);
    const estado = liquidacion === undefined ? (esHoy ? 'open' : null) : liquidacion.status;
    const periodo = claveMes(mes);
    // Las comisiones del mes (y del negocio, si viene de «Ver sus comisiones»), cada combinación su petición.
    const delMes = usePorClave(
        `${periodo}|${negocio ?? ''}`,
        (clave) => {
            const [period = '', businessId = ''] = clave.split('|');
            return d.ops.listPartnerCommissions({ period, business_id: businessId === '' ? undefined : businessId, limit: 100 }).then((r) => r.data);
        },
        d.onSesionCaducada,
    );
    const comisiones = comisionesDelMes(delMes.datos ?? [], mes);
    // El resumen del mes elegido, también de los anteriores (`period`, G1105), y del negocio elegido (`business_id`, G1177).
    const delResumen = usePorClave(
        `${periodo}|${negocio ?? ''}`,
        (clave) => {
            const [period = '', businessId = ''] = clave.split('|');
            return d.ops.getPartnerSummary(period, businessId === '' ? undefined : businessId);
        },
        d.onSesionCaducada,
    );
    const resumen = delResumen.datos;
    // El nombre del negocio filtrado: de sus comisiones o de las del mes en curso; si no tiene ninguna, genérico.
    const nombreNegocio =
        negocio === null ? null : ([...(delMes.datos ?? []), ...(d.comisiones.datos?.data ?? [])].find((c) => c.business_id === negocio)?.business_name ?? 'Este negocio');
    const anterior = { y: mes.m === 0 ? mes.y - 1 : mes.y, m: mes.m === 0 ? 11 : mes.m - 1 };
    const cambio = variacion(resumen?.month_collected_change_bp);
    // Barras del año del mes elegido: una por liquidación, de enero a diciembre.
    const delAno = liquidaciones
        .map((l) => ({ l, m: mesDePeriodo(l.period) }))
        .filter(({ m }) => m.y === mes.y)
        .sort((a, b) => a.m.m - b.m.m);
    const cargando = (d.liquidaciones.datos === null && d.liquidaciones.error === null) || (resumen === null && delResumen.error === null);

    return (
        <>
            {cargando ? (
                <div className="rv-pt-esqueleto" aria-busy="true" />
            ) : (
                <div className="rv-pt-cifras">
                    {resumen !== null && (
                        <TarjetaCifra titulo={nombreNegocio === null ? 'Cobrado a tus negocios' : `Cobrado a ${nombreNegocio}`} cifra={cifraDinero(resumen.month_collected)} moneda="€" pie={`${textoMes(mes)} · sin IVA`} />
                    )}
                    <TarjetaCifra
                        titulo="Tu comisión"
                        // Sin resumen, el total de la liquidación, que es de todos sus negocios: con uno elegido, nada.
                        cifra={resumen !== null ? cifraDinero(resumen.month_commission) : liquidacion === undefined || negocio !== null ? '0,00' : cifraDinero(liquidacion.total)}
                        moneda="€"
                        oscura
                        pie={cambio !== null ? `${cambio.texto} frente a ${nombreMes(anterior)}` : `${d.pct} de lo cobrado`}
                    />
                    {estado !== null && (
                        <TarjetaCifra
                            titulo="Estado"
                            cifra={estado === 'open' ? 'En curso' : estado === 'prepared' ? 'Cerrada' : 'Pagada'}
                            pie={estado === 'open' ? `Se cierra el 5 de ${sig}` : estado === 'prepared' ? `Se paga antes del 15 de ${sig}` : `Liquidación de ${nombreMes(mes)}`}
                        />
                    )}
                </div>
            )}
            {/* G1199: con un negocio elegido no sale: las liquidaciones son de todos tus negocios. */}
            {delAno.length > 0 && negocio === null && (
                <section className="rv-pt-tarjeta" aria-label="Tu comisión por mes">
                    <strong className="rv-pt-tarjeta__titulo">Tu comisión por mes</strong>
                    <BarrasMes
                        barras={delAno.map(({ l, m }) => ({ clave: claveMes(m), corta: mesCorto(m), larga: textoMes(m), valor: l.total.amount, cifra: dinero(l.total) }))}
                        elegida={claveMes(mes)}
                        onElegir={elegirMes}
                    />
                </section>
            )}
            {nombreNegocio !== null && (
                <Filtros
                    etiqueta="Negocio"
                    items={[{ etiqueta: 'Todos tus negocios' }, { etiqueta: nombreNegocio }]}
                    valor={1}
                    onCambio={quitarNegocio}
                />
            )}
            <div className="rv-pt-lista">
                {comisiones.length === 0 ? (
                    <p className="rv-pt-vacia">{delMes.datos === null && delMes.error === null ? '' : nombreNegocio === null ? 'Este mes no hubo cuotas de tus negocios.' : `Este mes ${nombreNegocio} no tuvo cuota.`}</p>
                ) : (
                    comisiones.map((c) => {
                        const e = estadoComision(c);
                        return <Fila key={c.id} icono="building-2" titulo={c.business_name} linea={lineaComision(c)} cifra={dinero(c.amount)} cifraApagada={e.noSuma} bajoCifra={e.texto} />;
                    })
                )}
            </div>
            <p className="rv-pt-pie">{`Tu comisión es el ${d.pct} de cada cuota cobrada, sin IVA. Si un negocio no paga o se le devuelve la cuota, ese mes no suma.`}</p>
        </>
    );
}

function Liquidaciones({ d }: { d: DatosPortal }) {
    const liquidaciones = d.liquidaciones.datos?.data ?? [];
    const enCurso = liquidacionEnCurso(liquidaciones);
    if (d.liquidaciones.datos === null) {
        return d.liquidaciones.error !== null ? <p className="rv-pt-nota">No hemos podido cargar tus liquidaciones. Prueba de nuevo.</p> : <div className="rv-pt-esqueleto" aria-busy="true" />;
    }
    return (
        <>
            {enCurso !== undefined && (
                <div className="rv-pt-cifras">
                    <TarjetaCifra
                        titulo="Próxima liquidación"
                        cifra={cifraDinero(enCurso.total)}
                        moneda="€"
                        oscura
                        pie={`${mayuscula(nombreMes(mesDePeriodo(enCurso.period)))} · te la pagamos antes del 15 de ${nombreMes(mesSiguiente(mesDePeriodo(enCurso.period)))}`}
                    />
                </div>
            )}
            <div className="rv-pt-lista">
                {liquidaciones.length === 0 ? (
                    <p className="rv-pt-vacia">Todavía no hay liquidaciones.</p>
                ) : (
                    liquidaciones.map((l) => {
                        const m = mesDePeriodo(l.period);
                        const factura = l.invoice_url ?? null;
                        const aria = `Descargar la factura de ${nombreMes(m)}${l.invoice_number != null ? ` (${l.invoice_number})` : ''}`;
                        return (
                            <Fila
                                key={l.id}
                                titulo={textoMes(m)}
                                linea={lineaLiquidacion(l)}
                                cifra={dinero(l.total)}
                                accion={
                                    factura !== null ? (
                                        <a className="rv-bi rv-pt-descargar" href={factura} target="_blank" rel="noopener noreferrer" download aria-label={aria} title={aria}>
                                            <Icono nombre="download" />
                                        </a>
                                    ) : (
                                        <span className="rv-pt-descargar" aria-hidden="true" />
                                    )
                                }
                            />
                        );
                    })
                )}
            </div>
            <p className="rv-pt-pie">La liquidación de un mes se cierra el día 5 del mes siguiente y te la pagamos por transferencia antes del día 15, con su factura.</p>
        </>
    );
}
